import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

/**
 * SearchHighlighterTipTap Extension
 * Allows highlighting search results in the Tiptap editor
 * Supports section-specific filtering for targeted results
 */
export const SearchHighlighterTipTap = Extension.create({
    name: 'searchHighlighter',

    addOptions() {
        return {
            highlightClass: 'search-result-highlight',
            currentResultClass: 'current'
        };
    },

    addStorage() {
        // Add storage for search results
        return {
            searchResults: [],
            currentResultIndex: -1,
            searchOptions: {}
        };
    },

    addCommands() {
        return {
            setSearchResults: (results, currentIndex = -1, options = {}) => ({ editor }) => {
                // Store the new search results and options
                editor.storage.searchHighlighter.searchResults = results || [];
                editor.storage.searchHighlighter.currentResultIndex = currentIndex;
                editor.storage.searchHighlighter.searchOptions = options;
                
                // Trigger a re-render to update decorations
                // Make this safe for testing environment
                if (editor.view?.dispatch) {
                    try {
                        // Use a basic dispatch if tr.setMeta is not available
                        if (editor.view.state?.tr?.setMeta) {
                            editor.view.dispatch(editor.view.state.tr.setMeta('forceUpdate', true));
                        } else {
                            editor.view.dispatch();
                        }
                    } catch (e) {
                        // Ignore errors in test environment
                    }
                }
                
                return true;
            },
            
            clearSearchResults: () => ({ editor }) => {
                editor.storage.searchHighlighter.searchResults = [];
                editor.storage.searchHighlighter.currentResultIndex = -1;
                editor.storage.searchHighlighter.searchOptions = {};
                
                // Trigger a re-render to update decorations
                // Make this safe for testing environment
                if (editor.view?.dispatch) {
                    try {
                        // Use a basic dispatch if tr.setMeta is not available
                        if (editor.view.state?.tr?.setMeta) {
                            editor.view.dispatch(editor.view.state.tr.setMeta('forceUpdate', true));
                        } else {
                            editor.view.dispatch();
                        }
                    } catch (e) {
                        // Ignore errors in test environment
                    }
                }
                
                return true;
            },
            
            navigateToPreviousSearchResult: () => ({ editor }) => {
                const { searchResults, currentResultIndex } = editor.storage.searchHighlighter;
                
                if (!searchResults || searchResults.length === 0) {
                    return false;
                }
                
                let newIndex;
                if (currentResultIndex <= 0) {
                    // Wrap around to the last result when at the beginning
                    newIndex = searchResults.length - 1;
                } else {
                    newIndex = currentResultIndex - 1;
                }
                
                editor.storage.searchHighlighter.currentResultIndex = newIndex;
                
                // Trigger a re-render to update decorations
                // Make this safe for testing environment
                if (editor.view?.dispatch) {
                    try {
                        // Use a basic dispatch if tr.setMeta is not available
                        if (editor.view.state?.tr?.setMeta) {
                            editor.view.dispatch(editor.view.state.tr.setMeta('forceUpdate', true));
                        } else {
                            editor.view.dispatch();
                        }
                    } catch (e) {
                        // Ignore errors in test environment
                    }
                }
                
                return true;
            },
            
            navigateToNextSearchResult: () => ({ editor }) => {
                const { searchResults, currentResultIndex } = editor.storage.searchHighlighter;
                
                if (!searchResults || searchResults.length === 0) {
                    return false;
                }
                
                let newIndex;
                if (currentResultIndex >= searchResults.length - 1) {
                    // Wrap around to the first result when at the end
                    newIndex = 0;
                } else {
                    newIndex = currentResultIndex + 1;
                }
                
                editor.storage.searchHighlighter.currentResultIndex = newIndex;
                
                // Trigger a re-render to update decorations
                // Make this safe for testing environment
                if (editor.view?.dispatch) {
                    try {
                        // Use a basic dispatch if tr.setMeta is not available
                        if (editor.view.state?.tr?.setMeta) {
                            editor.view.dispatch(editor.view.state.tr.setMeta('forceUpdate', true));
                        } else {
                            editor.view.dispatch();
                        }
                    } catch (e) {
                        // Ignore errors in test environment
                    }
                }
                
                return true;
            }
        };
    },

    addProseMirrorPlugins() {
        const { highlightClass, currentResultClass } = this.options;
        
        // Create a plugin key - needs to be unique
        const key = new PluginKey('search-highlighter');

        return [
            new Plugin({
                key,
                props: {
                    decorations: (state) => {
                        // Safe access for testing environment
                        if (!this.editor?.storage?.searchHighlighter) {
                            return DecorationSet.empty;
                        }
                        
                        const { searchResults, currentResultIndex, searchOptions } = this.editor.storage.searchHighlighter;
                        
                        if (!searchResults || !searchResults.length) {
                            return DecorationSet.empty;
                        }

                        const decorations = [];
                        const doc = state.doc;
                        
                        // Use text node traversal approach to find actual text in the document
                        // This ensures we're finding all actual instances regardless of DOM structure
                        const allMatches = [];
                        
                        // Extract the search text from the first result
                        let query = searchResults.length > 0 ? searchResults[0].text : '';
                        if (!query || query.length === 0) return DecorationSet.empty;
                        
                        // Get case sensitivity from search options
                        const caseSensitive = searchOptions?.caseSensitive ?? false;
                        
                        // Check if this is an 'all sections' search
                        // If ANY result has section.type === 'all', then this is an all-sections search
                        const isAllSectionsSearch = searchResults.some(result => 
                            !result.section || !result.section.type || result.section.type === 'all'
                        );
                        
                        // Get section filter info only if this is NOT an all-sections search
                        const sectionFilter = isAllSectionsSearch ? null : 
                            (searchResults.length > 0 && 
                            searchResults[0].section && 
                            searchResults[0].section.type ? 
                                searchResults[0].section.type : null);
                        
                        console.log('[SearchHighlighter] Search mode:', {
                            isAllSectionsSearch,
                            sectionFilter,
                            query,
                            resultsCount: searchResults.length
                        });
                        
                        // Pre-compute section boundaries if we're doing section-specific search
                        const sectionBoundaries = [];
                        if (sectionFilter) {
                            // First pass: find all section headers and their positions
                            doc.descendants((node, pos) => {
                                if (node.type.name === 'heading') {
                                    const attrs = node.attrs || {};
                                    let sectionType = attrs.sectionType || '';
                                    
                                    // Try to extract from class if sectionType is not set
                                    if (!sectionType && attrs.class && attrs.class.includes('section-')) {
                                        const classMatch = attrs.class.match(/section-([a-zA-Z0-9_-]+)/);
                                        if (classMatch) {
                                            sectionType = classMatch[1];
                                        }
                                    }
                                    
                                    if (sectionType) {
                                        sectionBoundaries.push({
                                            type: sectionType,
                                            startPos: pos + node.nodeSize, // Content starts after the header
                                            headerPos: pos
                                        });
                                    }
                                }
                            });
                            
                            // Sort by position
                            sectionBoundaries.sort((a, b) => a.headerPos - b.headerPos);
                            
                            // Add end positions
                            for (let i = 0; i < sectionBoundaries.length; i++) {
                                if (i < sectionBoundaries.length - 1) {
                                    sectionBoundaries[i].endPos = sectionBoundaries[i + 1].headerPos;
                                } else {
                                    sectionBoundaries[i].endPos = doc.content.size;
                                }
                            }
                            
                            console.log('[SearchHighlighter] Section boundaries:', sectionBoundaries);
                        }
                        
                        // Find all occurrences in the document by traversing text nodes
                        doc.descendants((node, pos) => {
                            if (node.isText) {
                                const nodeText = node.text;
                                const searchQuery = caseSensitive ? query : query.toLowerCase();
                                const searchText = caseSensitive ? nodeText : nodeText.toLowerCase();
                                
                                // Check if this node is in the specified section
                                let isInTargetSection = !sectionFilter; // Default to true if no section filter
                                let currentSection = 'all';
                                
                                if (!sectionFilter) {
                                    // No section filtering needed - always include this node
                                    let index = 0;
                                    let continueSearching = true;
                                    while (continueSearching) {
                                        // Search based on case sensitivity setting
                                        index = searchText.indexOf(searchQuery, index);
                                        if (index === -1) {
                                            continueSearching = false;
                                            break;
                                        }
                                        
                                        // Actual position in the document
                                        const from = pos + index;
                                        const to = pos + index + query.length;
                                        
                                        // Store this match
                                        allMatches.push({ 
                                            from, 
                                            to,
                                            section: 'all'
                                        });
                                        
                                        // Move past this occurrence
                                        index += query.length;
                                    }
                                    
                                    return;
                                }
                                
                                // Section filtering is active - determine if this node is in the target section
                                if (sectionFilter && sectionBoundaries.length > 0) {
                                    // Find which section this text node belongs to
                                    let nodeSection = null;
                                    
                                    for (const section of sectionBoundaries) {
                                        if (pos >= section.startPos && pos < section.endPos) {
                                            nodeSection = section.type;
                                            break;
                                        }
                                    }
                                    
                                    // If no section found, this is unsectioned content
                                    if (!nodeSection) {
                                        // Check if position is before first section
                                        if (sectionBoundaries.length > 0 && pos < sectionBoundaries[0].headerPos) {
                                            nodeSection = 'unsectioned';
                                        }
                                    }
                                    
                                    currentSection = nodeSection || 'unsectioned';
                                    
                                    // Check if this section matches the filter
                                    if (nodeSection && nodeSection !== 'unsectioned') {
                                        const matchesFilter = 
                                            nodeSection === sectionFilter || 
                                            nodeSection.includes(sectionFilter) || 
                                            sectionFilter.includes(nodeSection);
                                            
                                        isInTargetSection = matchesFilter;
                                    } else {
                                        // Unsectioned content is excluded from section-specific searches
                                        isInTargetSection = false;
                                    }
                                    
                                    if (!isInTargetSection) {
                                        return;
                                    }
                                }
                                
                                // Perform the actual search in this text node
                                let index = 0;
                                let continueSearching = true;
                                while (continueSearching) {
                                    // Search based on case sensitivity setting
                                    index = searchText.indexOf(searchQuery, index);
                                    if (index === -1) {
                                        continueSearching = false;
                                        break;
                                    }
                                    
                                    // Actual position in the document
                                    const from = pos + index;
                                    const to = pos + index + query.length;
                                    
                                    // Store this match
                                    allMatches.push({ 
                                        from, 
                                        to,
                                        section: currentSection
                                    });
                                    
                                    // Move past this occurrence
                                    index += query.length;
                                }
                            }
                        });
                        
                        // Create decorations for each match found
                        allMatches.forEach((match, index) => {
                            try {
                                // Determine if this is the current result
                                const isCurrent = index === currentResultIndex;
                                
                                // Apply the decoration
                                const decoration = Decoration.inline(
                                    match.from,
                                    match.to,
                                    { 
                                        class: `${highlightClass}${isCurrent ? ' ' + currentResultClass : ''}`,
                                        'data-search-result': 'true',
                                        'data-search-index': index,
                                        'data-section': match.section || 'all'
                                    }
                                );
                                decorations.push(decoration);
                            } catch (error) {
                                console.error('Error creating search decoration:', error);
                            }
                        });

                        return DecorationSet.create(doc, decorations);
                    }
                }
            })
        ];
    }
});

export default SearchHighlighterTipTap;